import styled, { css } from "styled-components";
import { breakpoints } from "../../../../../shared-logic/themes/lexusTheme";
import type { ListVariantsType } from "./List";

// STYLES
const commonStyles = css`
    &[hidden] {
        display: none;
    }
`;

const basket = css`
    ${commonStyles};
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
        padding-inline: 0;
        padding-block: 0 calc(var(--spacing-3) / 2);
        border-bottom: 1px solid var(--color-mist);

        @supports not (padding-block: 0) {
            &:not(:nth-last-child(1)) {
                padding-bottom: calc(var(--spacing-3) / 2) !important;
            }
        }
    }
`;

const basketSubList = css`
    ${commonStyles};
    display: flex;
    flex-direction: column;
    row-gap: var(--spacing-1);
    margin: 0;
    padding: var(--spacing-2);
    list-style: none;
    background-color: var(--color-white);
    border: 1px solid var(--color-silver);
    border-radius: 4px;

    li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
        padding: 0;
        border: 0;

        /* Text element in list */
        & > span {
            display: inline-flex;
            align-items: center;
            column-gap: calc(var(--spacing-1) / 2);
        }
    }

    @supports not (padding-inline: 0) {
        li {
            &:not(:nth-last-child(1)) {
                margin-bottom: var(--spacing-1) !important;
            }
            & > span:not(:nth-last-child(1)) {
                margin-bottom: calc(var(--spacing-1) / 2) !important;
            }
        }
    }
`;

const basketPanelList = css`
    ${commonStyles};
    display: flex;
    flex-direction: column;
    row-gap: calc(var(--spacing-3) / 2);
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0;

        @supports not (padding-inline: 0) {
            &:not(:nth-last-child(1)) {
                margin-bottom: calc(var(--spacing-3) / 2) !important;
            }
        }
    }
`;

const specs = css`
    ${commonStyles};
    display: flex;
    flex-direction: column;
    row-gap: var(--spacing-1);
    margin: 0;
    padding: 0;
    list-style: none;

    li,
    & > div {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 0;

        @supports not (padding-inline: 0) {
            &:not(:nth-last-child(1)) {
                margin-bottom: var(--spacing-1) !important;
            }
        }
    }
`;

const usp = css<{ columns?: number }>`
    ${commonStyles};
    display: flex;
    flex-wrap: wrap;
    row-gap: var(--spacing-1);
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: inline-flex;
        align-items: flex-start;
        width: 100%;
        font-family: var(--font-book);
        font-size: 1.3rem;
        line-height: 2rem;

        @media screen and (min-width: ${breakpoints.lg}) {
            width: auto;
        }

        ${({ columns }) =>
            columns &&
            css`
                @media screen and (min-width: ${breakpoints.lg}) {
                    flex: 1 0 calc(100% / ${columns});
                }
            `};

        @supports not (padding-inline: 0) {
            &:not(:nth-last-child(1)) {
                margin-bottom: var(--spacing-1) !important;
            }
        }
    }

    li::before {
        display: block;
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        margin-inline-end: var(--spacing-1);
        content: "";
        background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDIwIDIwIiBmaWxsPSJub25lIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xNS43ODg5IDYuMjc5MjlMNy43NTYxMiAxNC41NzEyTDQuMjE4NzUgMTEuMDMzOEw0Ljc4NDQ0IDEwLjQ2ODFMNy43NDcwNyAxMy40MzA4TDE1LjIxNDMgNS43MjI2NkwxNS43ODg5IDYuMjc5MjlaIiBmaWxsPSIjMzYzODQwIi8+Cjwvc3ZnPg==");
        background-repeat: no-repeat;
        background-position: center;
        background-size: 20px 20px;

        @supports not (margin-inline: 0) {
            margin-right: var(--spacing-1);
        }
    }

    li::after {
        display: table;
        clear: both;
        content: "";
    }
`;

const bullet = css<{ columns?: number }>`
    ${commonStyles};
    margin: 0;
    list-style: none;
    padding-inline-start: calc(var(--spacing-5) / 2);

    ${(props) =>
        props.columns &&
        css`
            @media screen and (min-width: ${breakpoints.xl}) {
                columns: ${props.columns};
                column-gap: var(--spacing-4);
            }
        `};

    li {
        position: relative;
        font-family: var(--font-book);
        font-size: 1.8rem;
        line-height: 3rem;

        &:not(:last-child) {
            margin-bottom: 8px !important;
        }

        /* Custom marker */
        &::before {
            display: block;
            position: absolute;
            top: 12px;
            left: -18px;
            width: 4px;
            height: 4px;
            content: "";
            background-color: var(--color-sky-blue);
            border-radius: 50%;
        }
    }

    @supports not (padding-inline: 0) {
        padding-left: calc(var(--spacing-5) / 2);
    }
`;

const unstyled = css<{ columns?: number }>`
    ${commonStyles};
    padding-inline-start: 0;
    list-style: none;

    ${(props) =>
        props.columns &&
        css`
            @media screen and (min-width: ${breakpoints.xl}) {
                columns: ${props.columns};
                column-gap: var(--spacing-4);
            }
        `};

    li {
        break-inside: avoid;
        &:not(:nth-last-child(1)) {
            margin-bottom: var(--spacing-1);
        }
    }

    @supports not (padding-inline: 0) {
        padding-left: 0;
    }
`;

// COMPONENT
export const ListVariants = {
    basket,
    "basket-sub-list": basketSubList,
    "basket-panel-list": basketPanelList,
    specs,
    usp,
    bullet,
    unstyled,
};

const ListVariant = styled.ol<{ variant: ListVariantsType; columns?: number; skeleton?: boolean }>`
    ${({ variant }) =>
        variant &&
        css`
            ${ListVariants[variant]};
        `};
`;

export default ListVariant;
